import React, { useState, useEffect, useRef } from "react";
import { Callout, Button, Spinner, TextArea } from "@blueprintjs/core";
import { useProjects } from "../auth/ProjectContext";

interface ChatMessage {
  role: string;
  content: string;
}

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";

interface Props {
  compact?: boolean;
}

export default function OpenAiChat({ compact }: Props) {
  const { currentProject } = useProjects?.() || {};
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "system",
      content: "You are a helpful assistant for certification basis management.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, scrollRef]);

  if (!currentProject) {
    return <Callout intent="warning">Select a project from the switcher.</Callout>;
  }

  const handleSend = async () => {
    if (!input.trim()) return;
    setLoading(true);
    setError(null);
    const userMsg = { role: "user", content: input };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    try {
      const res = await fetch(`${API_BASE}/openai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: newMessages, projectId: currentProject.id }),
      });
      const data = await res.json();
      if (res.ok && data.reply) {
        setMessages([...newMessages, data.reply]);
      } else {
        setError(data.error || "Unknown error from OpenAI");
      }
    } catch {
      setError("Network error");
    }
    setLoading(false);
  };

  const qaPairs = [] as { question: string; answer: string }[];
  for (let i = 1; i < messages.length; i += 2) {
    const q = messages[i];
    const a = messages[i + 1];
    if (q && q.role === "user") {
      qaPairs.push({ question: q.content, answer: a?.content || "" });
    }
  }

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: compact ? 280 : 700,
        margin: compact ? 0 : "0 auto",
      }}
    >
      {!compact && <h2 style={{ margin: "0.5rem" }}>OpenAI Assistant</h2>}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "0.5rem" }}>
        {qaPairs.map((p, i) => (
          <details key={i} style={{ marginBottom: 12 }}>
            <summary>
              <b>You:</b> {p.question}
            </summary>
            <div style={{ marginTop: 8 }}>
              <b>AI:</b> {p.answer}
            </div>
          </details>
        ))}
        {loading && <Spinner size={20} style={{ margin: 8 }} />}
        {error && <Callout intent="danger">{error}</Callout>}
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "0.5rem",
          borderTop: "1px solid #d8e1e8",
        }}
      >
        <TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type your message..."
          fill
          style={{ resize: "vertical", minHeight: 48 }}
          disabled={loading}
        />
        <Button
          intent="primary"
          onClick={handleSend}
          loading={loading}
          disabled={!input.trim() || loading}
          style={{ minWidth: 100 }}
        >
          Send
        </Button>
      </div>
    </div>
  );
}
